import { useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import { UserTier, FeatureType } from '@/models/feature-restriction.model';
import { hasFeatureAccess, getRequiredTier, getAvailableFeatures } from '@/constants/FeatureAccessConfig';

export const useFeatureAccess = () => {
  const user = useAppSelector((state) => state.auth.user);
  
  // Get user tier from subscription or default to 'free'
  const userTier: UserTier = useMemo(() => {
    if (!user?.subscription?.plan_id) {
      return 'free';
    }
    
    const planId = user.subscription.plan_id.toLowerCase();
    
    if (planId === 'test_id') return 'free';
    if (planId === 'pro_id') return 'pro';
    if (planId === 'advisory_id') return 'enterprise';
    if (planId === 'startup_id') return 'startup';
    
    return 'free';
  }, [user?.subscription?.plan_id]);

  // Check if user has access to a specific feature
  const hasAccess = (feature: FeatureType): boolean => {
    return hasFeatureAccess(feature, userTier);
  };

  // Get the minimum tier required for a feature
  const getRequiredTierForFeature = (feature: FeatureType): UserTier => {
    return getRequiredTier(feature);
  };

  // Get all available features for current user tier
  const availableFeatures = useMemo(() => {
    return getAvailableFeatures(userTier);
  }, [userTier]);

  // Check if user can upgrade to access a feature
  const canUpgrade = (feature: FeatureType): boolean => {
    const requiredTier = getRequiredTierForFeature(feature);
    const tierHierarchy: UserTier[] = ['free', 'pro', 'startup', 'enterprise'];
    const currentTierIndex = tierHierarchy.indexOf(userTier);
    const requiredTierIndex = tierHierarchy.indexOf(requiredTier);
    
    return currentTierIndex < requiredTierIndex;
  };

  // Get upgrade suggestions based on current tier
  const getUpgradeSuggestions = (): FeatureType[] => {
    const allFeatures: FeatureType[] = [
      'custom_domain',
      'advanced_analytics',
      'api_access',
      'team_collaboration',
      'priority_support',
      'unlimited_projects',
      'custom_integrations',
      'white_label',
      'advanced_deployment',
      'backup_restore',
      'sso_integration',
      'webhook_access',
      'advanced_ai_features',
      'custom_branding',
      'export_data',
      'credit_topup',
    ];

    return allFeatures.filter(feature => !hasAccess(feature) && canUpgrade(feature));
  };

  return {
    userTier,
    hasAccess,
    getRequiredTierForFeature,
    availableFeatures,
    canUpgrade,
    getUpgradeSuggestions,
    isFreeTier: userTier === 'free',
    isProTier: userTier === 'pro',
    isStartupTier: userTier === 'startup',
    isEnterpriseTier: userTier === 'enterprise',
  };
};
